export function initTestimonials() {
    const sections = document.querySelectorAll('.js-testimonials');

    if (!sections.length) {
        return;
    }

    sections.forEach((section) => {
        const viewport = section.querySelector('.js-testimonials-viewport');
        const track = section.querySelector('.js-testimonials-track');
        const cards = Array.from(section.querySelectorAll('.testimonials__card'));
        const prevButton = section.querySelector('.js-testimonials-prev');
        const nextButton = section.querySelector('.js-testimonials-next');
        const dotsWrap = section.querySelector('.js-testimonials-dots');

        if (!viewport || !track || !cards.length) {
            return;
        }

        let pageCount = 1;
        let slidePositions = [];
        let currentPage = 0;
        let rafId = null;
        const targetHeight = 290;
        // If any card needs more than targetHeight, we bump all cards to bumpHeight.
        // If content still overflows bumpHeight, we enable internal scroll on that card.
        const bumpHeight = 345;
        const mobileHeight = 400;
        const isMobile = () => window.matchMedia('(max-width: 819px)').matches;

        const getPxValue = (value, fallback) => {
            const parsed = parseFloat(value);
            return Number.isNaN(parsed) ? fallback : parsed;
        };

        const fitCardWidth = (card) => {
            const styles = window.getComputedStyle(card);
            const minWidth = getPxValue(styles.minWidth, 300);
            const maxWidth = getPxValue(styles.maxWidth, 600);
            const step = 12;
            const baseHeight = isMobile() ? mobileHeight : targetHeight;

            card.style.height = 'auto';
            if (isMobile()) {
                card.style.width = '';
            } else {
                card.style.width = `${minWidth}px`;
            }
            card.style.overflow = 'hidden';

            let currentWidth = minWidth;
            let currentHeight = card.getBoundingClientRect().height;

            if (!isMobile()) {
                while (currentHeight > baseHeight && currentWidth < maxWidth) {
                    currentWidth = Math.min(currentWidth + step, maxWidth);
                    card.style.width = `${currentWidth}px`;
                    currentHeight = card.getBoundingClientRect().height;
                }
            }

            const exceededAtMax = currentHeight > baseHeight && (!isMobile() ? currentWidth >= maxWidth : true);
            if (exceededAtMax) {
                card.style.height = `${isMobile() ? mobileHeight : bumpHeight}px`;
                if (card.scrollHeight > card.clientHeight) {
                    card.style.overflow = 'auto';
                }
            } else {
                card.style.height = `${baseHeight}px`;
            }

            return {
                card,
                exceeded: exceededAtMax,
            };
        };

        const updateMetrics = () => {
            const results = cards.map((card) => fitCardWidth(card));
            const needsBump = results.some((item) => item.exceeded);
            const uniformHeight = isMobile() ? mobileHeight : (needsBump ? bumpHeight : targetHeight);

            results.forEach((item) => {
                item.card.style.height = `${uniformHeight}px`;
                if (item.card.scrollHeight > uniformHeight) {
                    item.card.style.overflow = 'auto';
                } else if (!item.exceeded) {
                    item.card.style.overflow = 'hidden';
                }
            });
            const viewportWidth = viewport.getBoundingClientRect().width;
            const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
            if (isMobile()) {
                slidePositions = cards.map((card) => {
                    const cardRect = card.getBoundingClientRect();
                    const cardWidth = cardRect.width;
                    const offsetLeft = card.offsetLeft;
                    const centerOffset = (viewportWidth - cardWidth) / 2;
                    return clampScroll(Math.max(0, offsetLeft - centerOffset));
                });
                slidePositions = [...new Set(slidePositions.map((pos) => Math.round(pos)))];
                pageCount = slidePositions.length || 1;
            } else {
                slidePositions = cards
                    .map((card) => Math.round(clampScroll(card.offsetLeft)))
                    .filter((value, index, arr) => arr.indexOf(value) === index);
                if (!slidePositions.length) {
                    slidePositions = [0];
                }
                if (!slidePositions.includes(Math.round(maxScroll))) {
                    slidePositions.push(Math.round(maxScroll));
                }
                pageCount = slidePositions.length || 1;
            }
        };

        const clampScroll = (left) => {
            const maxScroll = viewport.scrollWidth - viewport.clientWidth;
            return Math.max(0, Math.min(left, maxScroll));
        };

        const scrollToPage = (page) => {
            const maxPage = pageCount - 1;
            let nextPage = page;
            if (isMobile()) {
                if (nextPage < 0) {
                    nextPage = maxPage;
                } else if (nextPage > maxPage) {
                    nextPage = 0;
                }
            } else {
                nextPage = Math.max(0, Math.min(nextPage, maxPage));
            }
            const targetLeft = slidePositions.length ? slidePositions[nextPage] : 0;
            viewport.scrollTo({
                left: targetLeft,
                behavior: 'smooth',
            });
        };

        const updateButtons = () => {
            if (prevButton) {
                prevButton.disabled = !isMobile() && currentPage <= 0;
            }
            if (nextButton) {
                nextButton.disabled = !isMobile() && currentPage >= pageCount - 1;
            }
        };

        const updateDots = () => {
            if (!dotsWrap) {
                return;
            }
            const dots = dotsWrap.querySelectorAll('.testimonials__dot');
            dots.forEach((dot, index) => {
                const isActive = index === currentPage;
                dot.classList.toggle('is-active', isActive);
                dot.setAttribute('aria-current', isActive ? 'true' : 'false');
            });
        };

        const setActiveFromScroll = () => {
            if (!slidePositions.length) {
                currentPage = 0;
            } else {
                const distances = slidePositions.map((pos) => Math.abs(viewport.scrollLeft - pos));
                const minIndex = distances.indexOf(Math.min(...distances));
                currentPage = minIndex;
            }
            currentPage = Math.max(0, Math.min(currentPage, pageCount - 1));
            updateDots();
            updateButtons();
        };

        const buildDots = () => {
            if (!dotsWrap) {
                return;
            }
            dotsWrap.innerHTML = '';
            for (let i = 0; i < pageCount; i += 1) {
                const dot = document.createElement('button');
                dot.type = 'button';
                dot.className = 'testimonials__dot';
                dot.setAttribute('aria-label', `Go to testimonials page ${i + 1}`);
                dot.addEventListener('click', () => scrollToPage(i));
                dotsWrap.appendChild(dot);
            }
        };

        const clampQuoteText = () => {
            const quotes = Array.from(section.querySelectorAll('.testimonials__quote'));
            quotes.forEach((quote) => {
                if (!quote.dataset.fullText) {
                    quote.dataset.fullText = quote.textContent.trim();
                }
                quote.textContent = quote.dataset.fullText;
            });
        };

        const handleScroll = () => {
            if (rafId) {
                return;
            }
            rafId = window.requestAnimationFrame(() => {
                rafId = null;
                setActiveFromScroll();
            });
        };

        if (prevButton) {
            prevButton.addEventListener('click', () => scrollToPage(currentPage - 1));
        }

        if (nextButton) {
            nextButton.addEventListener('click', () => scrollToPage(currentPage + 1));
        }

        viewport.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('resize', () => {
            updateMetrics();
            buildDots();
            setActiveFromScroll();
        });

        const runLayoutPass = () => {
            updateMetrics();
            buildDots();
            if (isMobile() && pageCount > 1) {
                currentPage = 1;
                scrollToPage(1);
            } else {
                setActiveFromScroll();
            }
        };

        clampQuoteText();
        runLayoutPass();

        window.addEventListener('load', () => {
            clampQuoteText();
            runLayoutPass();
        });
        if (document.fonts && document.fonts.ready) {
            document.fonts.ready
                .then(() => {
                    clampQuoteText();
                    runLayoutPass();
                })
                .catch(() => {});
        }
    });
}
